import React, { type FunctionComponent, Fragment, useState } from 'react'
import { css } from '@emotion/react'
import { Button, spacing } from '@retailer-platform/shared-components'
import {
  type HeroBannerBannerType,
  type HeroBannerFormData,
} from 'domains/merchandising/types/heroBanner.types'
import { HeroBannerForm } from 'domains/merchandising/components/hero-banner-form/HeroBannerForm'
import { HeroBannerCreateTypeSelector } from 'domains/merchandising/components/hero-banner-create/components/HeroBannerCreateTypeSelector'
import { HeroBannerBreadcrumbs } from 'domains/merchandising/components/hero-banner-breadcrumbs/HeroBannerBreadcrumbs'
import { useDashMessage } from 'intl/intl.hooks'
import { useCreateHeroBanner } from 'domains/merchandising/api/hero-banners/createHeroBanner.hooks'
import { useRetailerId } from 'utils/routing/params.hooks'
import { useGoToPath } from 'utils/routing/navigation.hooks'
import { mapFormDataToInput } from 'domains/merchandising/utils/heroBannerUtils'

const pageContainer = css({
  margin: `${spacing.X32} auto`,
  paddingLeft: spacing.X20,
  paddingRight: spacing.X20,
  maxWidth: '960px',
  width: '100%',
})

const confirmButtonContainer = css({
  display: 'flex',
  justifyContent: 'flex-end',
})

const confirmButtonStyles = css({
  // Override default button margin
  margin: `${spacing.X24} 0 0 0`,
})

export const HeroBannerCreate: FunctionComponent = () => {
  const continueButtonText = useDashMessage('merchandising.hero-banners.page.create.continueButton')

  const { createHeroBanner, apiLoading, apiError, apiSuccess } = useCreateHeroBanner()

  // TODO: Go to newly created details page ?
  const goToList = useGoToPath('merchandising-hero-banners-list')

  if (apiSuccess) {
    goToList()
  }

  const retailerId = useRetailerId()

  // Set the banner type when selecting a card
  const [bannerType, setBannerType] = useState<HeroBannerBannerType>(null)
  // Confirm the selection and move on to the next step
  const [selected, setSelected] = useState(false)

  const handleFormSubmit = (formData: HeroBannerFormData) => {
    // TODO: Form validation
    createHeroBanner({
      retailerId,
      heroBannerData: mapFormDataToInput(formData),
    })
  }

  return (
    <Fragment>
      <HeroBannerBreadcrumbs pageType={selected ? 'create' : 'bannerTypeSelector'} />
      <div css={pageContainer}>
        {selected ? (
          <HeroBannerForm
            type={bannerType}
            onFormSubmit={handleFormSubmit}
            submitError={apiError}
            isSubmitting={apiLoading}
            onCancel={goToList}
          />
        ) : (
          <div data-testid="heroBanner-banner-select-section">
            <HeroBannerCreateTypeSelector bannerType={bannerType} onBannerSelect={setBannerType} />
            <div css={confirmButtonContainer}>
              <Button
                data-testid="heroBanner-create-continue-button"
                disabled={!bannerType}
                onClick={() => setSelected(true)}
                css={confirmButtonStyles}
              >
                {continueButtonText}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Fragment>
  )
}
